import { c as _c } from "react/compiler-runtime";
import { postHostedUserDocumentsByIdProcessMutation } from '@onefootprint/fetch-hosted';
import { useRequestError } from '@onefootprint/fetch-hosted/use-request-error';
import type { DocumentResponse } from '@onefootprint/request-types';
import { useMutation } from '@tanstack/react-query';
import upperFirst from 'lodash/upperFirst';
import { useEffect, useRef, useState } from 'react';
import type { IdDocImageSide } from '@/idv2/features/collect-document/types';
import { getLogger, trackAction } from '@/idv2/utils/logger';
import { SLOW_CONNECTION_MESSAGE_TIMEOUT } from '../../../constants';
import useSubmitDoc from '../../../hooks/use-submit-doc';
import { IdDocImageUploadError } from '../../../types';
import { bytesToMegabytes } from '../../../utils/capture';
import getBackendErrorMessage from '../../../utils/get-backend-error-message';
import getRejectionCodes from '../../../utils/get-rejection-codes';
import useDocName from '../../hooks/use-doc-name';
import useIdDocMachine from '../../hooks/use-id-doc-machine';
import DesktopProcessingView from './desktop-processing-view';
const {
  logWarn,
  logInfo
} = getLogger({
  location: 'desktop-processing'
});
const DeskTopProcessing = () => {
  const $ = _c(69);
  const {
    getErrorMessage,
    getErrorCode
  } = useRequestError();
  const [state, dispatch] = useIdDocMachine();
  const submitDocMutation = useSubmitDoc();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = postHostedUserDocumentsByIdProcessMutation();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const processDocMutation = useMutation(t0);
  const [mode, setMode] = useState("loading");
  const [nextSide, setNextSide] = useState();
  const [showSlowConnectionMessage, setShowSlowConnectionMessage] = useState(false);
  const [retryLimitExceeded, setRetryLimitExceeded] = useState(false);
  const [isMissingRequirements, setIsMissingRequirements] = useState(false);
  const [step, setStep] = useState("upload");
  const slowConnectionTimer = useRef(null);
  const {
    idDoc: t1,
    image,
    authToken,
    currSide,
    id
  } = state.context;
  const {
    type,
    country
  } = t1;
  const isSelfie = currSide === "selfie";
  let t2;
  if ($[1] !== currSide || $[2] !== type) {
    t2 = {
      docType: type,
      imageType: currSide
    };
    $[1] = currSide;
    $[2] = type;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    getDocName,
    getSideName
  } = useDocName(t2);
  let t3;
  if ($[4] !== getDocName) {
    t3 = upperFirst(getDocName());
    $[4] = getDocName;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const docName = t3;
  let t4;
  if ($[6] !== getSideName) {
    t4 = upperFirst(getSideName());
    $[6] = getSideName;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const sideName = t4;
  let t5;
  if ($[8] !== currSide) {
    t5 = () => {
      trackAction(`document-processing-${currSide}:started`);
    };
    $[8] = currSide;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  useEffect(t5, t6);
  let t7;
  if ($[11] !== country || $[12] !== currSide || $[13] !== dispatch || $[14] !== docName || $[15] !== id || $[16] !== sideName || $[17] !== state.context.currSide || $[18] !== type) {
    t7 = data => {
      const {
        errorDetails,
        nextSideToCollect,
        isRetryLimitExceeded
      } = data;
      const rejectionCodes = getRejectionCodes(data);
      const rejectionContext = {
        rejectionCodes,
        docType: type,
        country,
        side: currSide
      };
      if (isRetryLimitExceeded) {
        logWarn(`Image upload retry limit exceeded. Side: ${sideName}, doc id: ${id}, type: ${docName}`);
        setRetryLimitExceeded(true);
        return;
      }
      if (nextSideToCollect === state.context.currSide) {
        logWarn("Document rejected", undefined, rejectionContext);
        trackAction(`document-processing-${currSide}:rejected`, rejectionContext);
        dispatch({
          type: "processingErrored",
          payload: {
            errors: errorDetails.map(_temp)
          }
        });
        return;
      }
      if (nextSideToCollect) {
        setNextSide(nextSideToCollect as IdDocImageSide);
      }
      setMode("success");
      trackAction(`document-processing-${currSide}:completed`);
    };
    $[11] = country;
    $[12] = currSide;
    $[13] = dispatch;
    $[14] = docName;
    $[15] = id;
    $[16] = sideName;
    $[17] = state.context.currSide;
    $[18] = type;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const handleProcessDocSuccess = t7;
  let t8;
  if ($[20] !== dispatch || $[21] !== getErrorMessage) {
    t8 = err_0 => {
      const backendMessage = getBackendErrorMessage(err_0);
      dispatch({
        type: "processingErrored",
        payload: {
          errors: [backendMessage ? {
            errorType: "custom_error",
            errorInfo: backendMessage
          } : {
            errorType: IdDocImageUploadError.networkError,
            errorInfo: getErrorMessage(err_0)
          }]
        }
      });
    };
    $[20] = dispatch;
    $[21] = getErrorMessage;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const handleError = t8;
  let t9;
  if ($[23] !== getErrorMessage || $[24] !== handleError || $[25] !== id) {
    t9 = err_1 => {
      handleError(err_1);
      logWarn(`Error while processing id-doc image ${id}: ${getErrorMessage(err_1)}`, err_1);
    };
    $[23] = getErrorMessage;
    $[24] = handleError;
    $[25] = id;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const handleProcessDocError = t9;
  let t10;
  if ($[27] !== currSide || $[28] !== getErrorCode || $[29] !== getErrorMessage || $[30] !== handleError || $[31] !== id) {
    t10 = err_2 => {
      const errorCode = getErrorCode(err_2);
      const errorMessage = getErrorMessage(err_2);
      if (errorCode === "E109") {
        setNextSide(undefined);
        setMode("success");
        return;
      }
      handleError(err_2);
      logWarn(`Id-doc image submit failed on phone flow. Side: ${currSide}, upload session id: ${id}. Error: ${errorMessage}`, err_2);
    };
    $[27] = currSide;
    $[28] = getErrorCode;
    $[29] = getErrorMessage;
    $[30] = handleError;
    $[31] = id;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  const handleSubmitDocError = t10;
  let t11;
  if ($[33] !== authToken || $[34] !== handleProcessDocError || $[35] !== handleProcessDocSuccess || $[36] !== id || $[37] !== processDocMutation) {
    t11 = async () => {
      if (!id) {
        return;
      }
      setStep("analyze");
      ;
      try {
        const response = await processDocMutation.mutateAsync({
          path: {
            id
          },
          headers: {
            "X-Fp-Authorization": authToken
          }
        });
        handleProcessDocSuccess(response);
      } catch (t12) {
        const error = t12;
        handleProcessDocError(error);
      }
    };
    $[33] = authToken;
    $[34] = handleProcessDocError;
    $[35] = handleProcessDocSuccess;
    $[36] = id;
    $[37] = processDocMutation;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  const handleSubmitDocSuccess = t11;
  let t12;
  if ($[39] !== authToken || $[40] !== country || $[41] !== currSide || $[42] !== handleSubmitDocError || $[43] !== handleSubmitDocSuccess || $[44] !== id || $[45] !== image || $[46] !== submitDocMutation || $[47] !== type) {
    t12 = () => {
      if (!image || !authToken || !type || !country || !currSide || !id) {
        setIsMissingRequirements(true);
        const error_0 = `Desktop web flow - id-doc image could not be processed due to missing requirements. Requirements - image: ${image ? "OK" : "undefined"}, auth token: ${authToken ? "OK" : "undefined"}, doc type: ${type ? "OK" : "undefined"}, country: ${country ? "OK" : "undefined"}, current side: ${currSide ? "OK" : "undefined"}, id: ${id ? "OK" : "undefined"}`;
        logWarn(error_0);
        return;
      }
      if (!slowConnectionTimer.current) {
        slowConnectionTimer.current = setTimeout(() => {
          if (submitDocMutation.isSuccess) {
            return;
          }
          setShowSlowConnectionMessage(true);
        }, SLOW_CONNECTION_MESSAGE_TIMEOUT);
      }
      const {
        imageFile,
        extraCompressed,
        captureKind,
        forcedUpload
      } = image;
      logInfo(`Uploading image with size ${bytesToMegabytes(imageFile.size)} MB and type ${imageFile.type} in POST request`);
      submitDocMutation.mutate({
        body: {
          fileData: imageFile
        },
        path: {
          id,
          side: currSide
        },
        headers: {
          "X-Fp-Authorization": authToken,
          "X-Fp-Is-Extra-Compressed": String(extraCompressed),
          "X-Fp-Is-Manual": String(captureKind === "manual"),
          "X-Fp-Is-App-Clip": "false",
          "X-Fp-Is-Instant-App": "false",
          "X-Fp-Is-Upload": String(captureKind === "upload"),
          "X-Fp-Is-Forced-Upload": String(forcedUpload)
        }
      }, {
        onSuccess: handleSubmitDocSuccess,
        onError: handleSubmitDocError,
        onSettled: () => {
          if (slowConnectionTimer.current) {
            clearTimeout(slowConnectionTimer.current);
            setShowSlowConnectionMessage(false);
          }
        }
      });
    };
    $[39] = authToken;
    $[40] = country;
    $[41] = currSide;
    $[42] = handleSubmitDocError;
    $[43] = handleSubmitDocSuccess;
    $[44] = id;
    $[45] = image;
    $[46] = submitDocMutation;
    $[47] = type;
    $[48] = t12;
  } else {
    t12 = $[48];
  }
  let t13;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = [];
    $[49] = t13;
  } else {
    t13 = $[49];
  }
  useEffect(t12, t13);
  let t14;
  if ($[50] !== dispatch) {
    t14 = () => dispatch({
      type: "retryLimitExceeded"
    });
    $[50] = dispatch;
    $[51] = t14;
  } else {
    t14 = $[51];
  }
  const handleRetryLimitExceeded = t14;
  let t15;
  if ($[52] !== dispatch || $[53] !== nextSide) {
    t15 = () => dispatch({
      type: "processingSucceeded",
      payload: {
        nextSideToCollect: nextSide
      }
    });
    $[52] = dispatch;
    $[53] = nextSide;
    $[54] = t15;
  } else {
    t15 = $[54];
  }
  const handleNextStep = t15;
  let t16;
  if ($[55] !== country || $[56] !== currSide || $[57] !== docName || $[58] !== handleNextStep || $[59] !== handleRetryLimitExceeded || $[60] !== isMissingRequirements || $[61] !== isSelfie || $[62] !== mode || $[63] !== retryLimitExceeded || $[64] !== showSlowConnectionMessage || $[65] !== sideName || $[66] !== step || $[67] !== type) {
    t16 = <DesktopProcessingView mode={mode} step={step} showSlowConnectionMessage={showSlowConnectionMessage} retryLimitExceeded={retryLimitExceeded} isMissingRequirements={isMissingRequirements} docName={docName} sideName={sideName} isSelfie={isSelfie} country={country} docType={type} currSide={currSide} onRetryLimitExceeded={handleRetryLimitExceeded} onNextStep={handleNextStep} />;
    $[55] = country;
    $[56] = currSide;
    $[57] = docName;
    $[58] = handleNextStep;
    $[59] = handleRetryLimitExceeded;
    $[60] = isMissingRequirements;
    $[61] = isSelfie;
    $[62] = mode;
    $[63] = retryLimitExceeded;
    $[64] = showSlowConnectionMessage;
    $[65] = sideName;
    $[66] = step;
    $[67] = type;
    $[68] = t16;
  } else {
    t16 = $[68];
  }
  return t16;
};
export default DeskTopProcessing;
function _temp(err) {
  return {
    errorType: err.code ?? "custom_error",
    errorInfo: err.message
  };
}